// Data access seam for the shared portfolio.
//
// Mirrors src/data/collection.js: consumers never call fetch directly, so the
// transport, error shape and auth handling live in one place.
//
// `credentials: 'include'` is mandatory on every call. The session is an
// httpOnly cookie; without it the browser omits the cookie and a signed-in user
// gets a 401.

import { apiUrl } from '../config';

/** Thrown for any non-2xx response. `status` lets callers treat 401 (session
 *  expired) differently from a genuine failure. `code` is the server's
 *  machine-readable reason where it gives one — the message is English, and a
 *  page that translates keys off the code rather than showing it. */
export class PortfolioError extends Error {
  constructor(message, status, code = null) {
    super(message);
    this.name = 'PortfolioError';
    this.status = status;
    this.code = code;
  }
}

export async function request(path, options = {}) {
  let response;
  try {
    response = await fetch(apiUrl(path), {
      credentials: 'include',
      headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
      ...options,
    });
  } catch {
    throw new PortfolioError('Could not reach the server.', 0);
  }

  if (response.status === 401) {
    throw new PortfolioError('Your session has expired. Please sign in again.', 401);
  }

  // The API always returns JSON. Anything else means a proxy or error page got
  // in the way, and parsing it would throw something unhelpful.
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new PortfolioError('Unexpected response from the server.', response.status);
  }

  const body = await response.json();
  if (!response.ok) {
    throw new PortfolioError(body?.error || 'Request failed.', response.status, body?.code ?? null);
  }
  return body.data ?? body;
}

/** Current holdings across every linked brokerage. */
export const getHoldings = ({ refresh = false } = {}) =>
  request(`holdings${refresh ? '?refresh=1' : ''}`);

/**
 * Transaction history for the Fidelity account.
 *
 * Its own call rather than a field on getHoldings: the holdings response is
 * cached whole and read on every page load, and this is unbounded history that
 * most readers never open. `start` and `end` are ISO dates (YYYY-MM-DD).
 *
 * Resolves to `{ activities, warnings }`.
 */
export const getActivities = ({ start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return request(`holdings/activities${query ? `?${query}` : ''}`);
};

/**
 * Forces a rebuild and waits for the result, WITHOUT depending on the forced
 * request's own response.
 *
 * The forced request frequently dies before the rebuild finishes. The chain is
 * CloudFront (30s origin read) -> API Gateway (30s integration timeout, a hard
 * cap on HTTP APIs) -> Lambda (60s), and a rebuild takes 26-62s because IBKR
 * generates the Flex statement on demand and we poll for it. Measured on the
 * live site: 26.5s one run, over a minute another. A coin flip.
 *
 * The Lambda is NOT cancelled when the gateway gives up. It runs to completion
 * and writes the cache, so the rebuild the reader asked for does happen — they
 * just never see it, because the request carrying it back was cut. That is the
 * whole bug: press refresh, watch nothing change, reload the page later and
 * find the data was fresh all along.
 *
 * So the forced request is fired and then ignored, and the answer comes from
 * polling the CHEAP cached read until `asOf` moves. Every build stamps a new
 * `asOf`, so a change is an unambiguous "the rebuild landed" — and if someone
 * else's refresh lands first, that snapshot is just as fresh.
 */
export async function refreshAndWait(read, options = {}) {
    const { previousAsOf = null, deadlineMs = 180_000, pollMs = 5_000 } = options;
    const startedAt = Date.now();
    const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Kick off the rebuild. A rejection here is expected — a gateway timeout is
    // the normal case — and must not be thrown: the work is still running.
    let forcedValue = null;
    let forcedDone = false;
    const forced = read({ refresh: true })
        .then((result) => { forcedValue = result; })
        .catch(() => {})
        .finally(() => { forcedDone = true; });

    while (Date.now() - startedAt < deadlineMs) {
        // Pace on the interval — but while the forced request is still in
        // flight, race it, so the lucky case where it beats the gateway
        // returns promptly instead of waiting out a poll.
        //
        // The `forcedDone` check is load-bearing. Racing a promise that has
        // already SETTLED resolves instantly on every lap, which turns this
        // into a hot spin against the server: measured at 117 requests in four
        // minutes before the flag was added.
        await (forcedDone ? pause(pollMs) : Promise.race([pause(pollMs), forced]));
        if (forcedValue) return forcedValue;

        // A plain read is the cached snapshot: fast, and never triggers work.
        const latest = await read().catch(() => null);
        if (latest?.asOf && latest.asOf !== previousAsOf) return latest;
    }

    // Out of patience. Whatever is current beats leaving the page on stale
    // data with no explanation.
    return forcedValue ?? read();
}

/** Linked connections. Never includes credentials. */
export const getConnections = () => request('holdings/connections');

export const addIbkrConnection = ({ label, queryId, token }) =>
  request('holdings/connections', {
    method: 'POST',
    body: JSON.stringify({ source: 'ibkr', label, queryId, token }),
  });

/** Names a connection, or clears the name with an empty string.
 *
 *  Works for managed connections too: the nickname lives in our own table, not
 *  in the configuration the managed connection came from. */
export const setConnectionNickname = (id, nickname) =>
  request(`holdings/connections/${encodeURIComponent(id)}/nickname`, {
    method: 'PUT',
    body: JSON.stringify({ nickname }),
  });

export const removeConnection = (id) =>
  request(`holdings/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Starts the Fidelity link and returns SnapTrade's hosted portal URL.
 *
 *  Unlike IBKR there is no form: Fidelity has no public API, so the user signs
 *  in to Fidelity on SnapTrade's own page and no credential of theirs ever
 *  reaches this app. */
export const startFidelityLink = () =>
  request('holdings/connections/fidelity/link', { method: 'POST' });

/**
 * Which brokerage account the Kanosei Index is measured on, and the accounts it
 * could be.
 *
 * Accounts, not connections: one IBKR Flex query can cover several accounts,
 * and the one the index lives in arrives through a managed connection that has
 * no database row at all. So this is its own endpoint rather than a field on
 * `getConnections`.
 */
export const getIndexAccount = () => request('holdings/index-account');

/** Designates the index account, or clears it with null and returns to
 *  deriving it from the holdings. */
export const setIndexAccount = (accountId) =>
    request('holdings/index-account', {
        method: 'PUT',
        body: JSON.stringify({ accountId }),
    });
